import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { CaretDown } from "@phosphor-icons/react";
import { Link } from "wouter";
import type { Application, AppViewStatus, Billing, Job } from "../../shared/types.ts";
import { Monogram, ToneDot, cn, ease } from "../ui/index.ts";
import { getApplications, getBilling, isJob } from "../lib/api.ts";
import { useApp } from "../lib/ctx.ts";
import { list, utc } from "../lib/format.ts";
import { Detail } from "./Detail.tsx";

type Known = Application & { job: Job };

export const SENT = new Set<AppViewStatus>(["pending", "queued", "applying", "submitted", "confirmed", "on_site"]);
const SENDING = new Set<AppViewStatus>(["pending", "queued", "applying"]);
const QUEUED = new Set<AppViewStatus>(["pending", "queued"]);
const DAYS_SHOWN = 5;

const known = (r: Application): r is Known => isJob(r.job);

/** Local midnight of the day an application went out. A clock ahead of ours counts as today. */
function dayOf(r: Application) {
  const d = new Date(Math.min(utc(r.submittedAt ?? r.updatedAt), Date.now()));
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function dayLabel(t: number) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const back = Math.round((today.getTime() - t) / 86_400_000);
  if (back === 0) return "Today";
  if (back === 1) return "Yesterday";
  return new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** The latest days anything went out, newest first. */
function byDay(rows: Application[]) {
  const days = new Map<number, Known[]>();
  for (const r of rows) {
    if (!known(r) || !SENT.has(r.status)) continue;
    const t = dayOf(r);
    days.set(t, [...(days.get(t) ?? []), r]);
  }
  return [...days].sort((a, b) => b[0] - a[0]).slice(0, DAYS_SHOWN);
}

/**
 * Every application, reloaded whenever the engine reports something or the counts move. Both tend to land together, so
 * a burst loads once and only the latest answer is kept. Off when the screen passes its own rows.
 */
function useRows(off: boolean) {
  const app = useApp();
  const [rows, setRows] = useState<Application[] | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const at = useRef(0);
  const load = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const n = ++at.current;
      getApplications().then((r) => n === at.current && setRows(r), () => {});
    }, 50);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    if (off) return;
    addEventListener("engine-event", load);
    return () => removeEventListener("engine-event", load);
  }, [off, load]);
  useEffect(() => {
    if (!off) load();
  }, [off, load, app.stats]);
  return rows;
}

/** The side column on Home and Applications: what went out each day. Loads its own rows unless the screen passes them. */
export function SentList({
  rows: given,
  onOpen,
  seeAll = false,
  className,
}: {
  rows?: Application[];
  onOpen?: (j: Job) => void;
  seeAll?: boolean;
  className?: string;
}) {
  const own = useRows(!!given);
  const [open, setOpen] = useState<Job | null>(null);
  const [billing, setBilling] = useState<Billing | null>(null);
  const capped = !!billing && billing.plan !== "max" && billing.used >= billing.limit;

  useEffect(() => {
    getBilling().then(setBilling, () => {});
  }, []);

  const rows = given ?? own;
  if (!rows) return null;
  const show = onOpen ?? setOpen;
  const days = byDay(rows);

  return (
    <section className={cn("flex flex-col gap-3", className)}>
      <div className="flex h-7 items-center justify-between gap-3">
        <h2 className="text-[17px] font-bold tracking-[-0.015em] text-ink">Sent</h2>
        {seeAll && days.length > 0 && (
          <Link href="/applications" className="text-[13px] font-semibold text-primary-ink underline-offset-2 hover:underline">
            See all
          </Link>
        )}
      </div>
      <div className="overflow-hidden rounded-card bg-surface shadow-card">
        {days.length ? (
          days.map(([t, d]) => <Day key={t} label={dayLabel(t)} rows={d} onOpen={show} />)
        ) : (
          <p className="p-4 text-[13px] text-ink-soft">Apply to a job and it shows up here.</p>
        )}
        {capped && (
          <p className="border-t border-line bg-sunk/60 p-4 text-[13px] text-ink-soft">
            You've used this month's applications.{" "}
            <Link href="/upgrade" className="font-semibold text-primary-ink underline-offset-2 hover:underline">
              Upgrade
            </Link>
          </p>
        )}
      </div>
      {!onOpen && <Detail job={open} onClose={() => setOpen(null)} />}
    </section>
  );
}

function JobLine({ job, onOpen, note }: { job: Job; onOpen: (j: Job) => void; note?: ReactNode }) {
  return (
    <div className="flex items-center gap-2 px-4 py-2">
      <button type="button" onClick={() => onOpen(job)} className="group flex min-w-0 flex-1 items-center gap-3 text-left">
        <Monogram name={job.company} url={job.jobUrl} size="sm" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13.5px] font-semibold text-ink group-hover:underline group-hover:decoration-primary group-hover:decoration-2 group-hover:underline-offset-4">
            {job.company}
          </span>
          <span className="block truncate text-[12.5px] text-muted">{job.title}</span>
        </span>
      </button>
      {note}
    </div>
  );
}

/** One day's applications: a stack of logos and a count that opens into the list. */
function Day({ label, rows, onOpen }: { label: string; rows: Known[]; onOpen: (j: Job) => void }) {
  const [open, setOpen] = useState(false);
  const n = rows.length;
  const waiting = rows.filter((r) => SENDING.has(r.status)).length;
  const queued = rows.filter((r) => QUEUED.has(r.status)).length;
  const sending = waiting - queued;
  const parts = [[n - waiting, "sent"], [sending, "sending"], [queued, "queued"]].filter(([k]) => k).map(([k, w]) => `${k} ${w}`);
  return (
    <div className="border-b border-line last:border-b-0">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex min-h-14 w-full items-center gap-3 px-4 py-2 text-left transition-[background-color] duration-150 hover:bg-sunk/70"
      >
        <span className="flex w-[76px] shrink-0">
          {rows.slice(0, 3).map((r, i) => (
            <Monogram key={r.job.id} name={r.job.company} url={r.job.jobUrl} size="sm" className={cn("ring-2 ring-surface", i > 0 && "-ml-2.5")} />
          ))}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5 text-[13.5px] font-semibold text-ink">
            <span className="truncate">{label}</span>
            {waiting > 0 && (
              <span className="shrink-0 text-tone-amber-ink">
                <ToneDot pulse={sending > 0} />
              </span>
            )}
          </span>
          <span className="block truncate text-[12.5px] text-muted">
            {waiting ? parts.join(", ") : `${n} sent · ${list([...new Set(rows.map((r) => r.job.company))], 2)}`}
          </span>
        </span>
        <CaretDown size={14} weight="bold" className={cn("shrink-0 text-muted transition-[rotate] duration-200", open && "rotate-180")} aria-hidden />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.ul
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease }}
            className="overflow-hidden"
          >
            {rows.map((r) => (
              <li key={r.job.id}>
                <JobLine
                  job={r.job}
                  onOpen={onOpen}
                  note={SENDING.has(r.status) ? <span className="shrink-0 text-[12px] font-semibold text-tone-amber-ink">{QUEUED.has(r.status) ? "Queued" : "Sending"}</span> : null}
                />
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
